// ABOUTME: Pure helper that prunes non-business days from Order Intake / Cancellation Lead Time form values.
// ABOUTME: Removes such days from standard.applicableDays and drops nonStandard entries referencing them.

import {DayIdentifier} from '../types/deliveryWindow'
import {
  NonStandardLeadTimeFormValues,
  OrderIntakeLeadTimeFormValues,
  StandardLeadTimeFormValues,
  toDayIdentifier,
} from '../types/orderIntakeLeadTime'

export interface PruneNonBusinessDaysResult {
  changed: boolean
  standard: StandardLeadTimeFormValues | null
  nonStandard: NonStandardLeadTimeFormValues[]
}

/**
 * Removes any day that is not a business day from the form's applicable days and
 * non-standard entries. Returns `changed: false` (and the original references) when
 * nothing needs pruning, so callers can avoid spuriously marking the form dirty.
 */
export const pruneNonBusinessDays = (
  values: Pick<OrderIntakeLeadTimeFormValues, 'standard' | 'nonStandard'>,
  nonBusinessDays: DayIdentifier[],
): PruneNonBusinessDaysResult => {
  const nonBusinessSet = new Set(nonBusinessDays)

  const isNonBusinessDay = (day: string): boolean => {
    const id = toDayIdentifier(day)
    return id !== undefined && nonBusinessSet.has(id)
  }

  let changed = false

  let standard = values.standard
  if (standard) {
    const prunedDays = standard.applicableDays.filter((d) => !isNonBusinessDay(d))
    if (prunedDays.length !== standard.applicableDays.length) {
      standard = {...standard, applicableDays: prunedDays}
      changed = true
    }
  }

  let nonStandard = values.nonStandard
  const prunedNonStandard = nonStandard.filter((ns) => !isNonBusinessDay(ns.day))
  if (prunedNonStandard.length !== nonStandard.length) {
    nonStandard = prunedNonStandard
    changed = true
  }

  return {changed, standard, nonStandard}
}
